import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowDown,
  ArrowUp,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Circle,
  Clock,
  AlertTriangle,
  List,
  Map as MapIcon,
  Plus,
  Star,
  UserPlus,
  X,
  Workflow,
  DollarSign,
} from 'lucide-react';
// @ts-ignore
import { Action, CardData, KanbanTaskStatus } from '../types';
import { isActionOverdue, parseActionDeadline } from '../utils/actionDeadline';
import { useProjectStore } from '../store/useProjectStore';
import { useAuth } from '../context/AuthContext';
import { useOrgMembers, type OrgMember } from '../hooks/useOrgMembers';
import { syncPlanActionToSupabase } from '../services/actionSyncService';
import { ProjectService } from '../services/projectService';

interface ActionListViewProps {
  actions: Action[];
  cards: CardData[];
  onActionClick: (id: string, event?: React.MouseEvent) => void;
  onEditAction?: (id: string) => void;
  onUpdateAction: (id: string, data: Partial<Action>) => void;
  onDeleteAction?: (id: string) => void;
  onContextMenu?: (id: string, event: React.MouseEvent) => void;
  selectedActionIds?: Set<string>;
  activeTab: 'all' | 'mine' | 'doing' | 'overdue' | 'done';
  groupBy: 'none' | 'status' | 'responsavel' | 'impacto';
  onNavigateToMatrix?: (actionId: string) => void;
  onNavigateToCard?: (cardId: string) => void;
  zoom?: number;
  focusedColumn?: 'responsavel' | 'prazo';
}

const UserAssignmentPopover: React.FC<{
  currentValue?: string;
  orgMembers?: OrgMember[];
  projectMembers?: OrgMember[];
  onSelectMember: (name: string, userId?: string) => void;
  onClose: () => void;
}> = ({ currentValue, orgMembers = [], projectMembers = [], onSelectMember, onClose }) => {
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const { actions, organization } = useProjectStore();

  const availableUsers = useMemo(() => {
    const seen = new Set<string>();
    const result: Array<{ name: string; userId?: string }> = [];

    for (const member of projectMembers) {
      if (!seen.has(member.name)) {
        seen.add(member.name);
        result.push({ name: member.name, userId: member.id });
      }
    }
    for (const member of orgMembers) {
      if (!seen.has(member.name)) {
        seen.add(member.name);
        result.push({ name: member.name, userId: member.id });
      }
    }
    (organization?.sectors ?? []).flatMap((s) => s.roles ?? []).forEach((role) => {
      if (!seen.has(role.name)) {
        seen.add(role.name);
        result.push({ name: role.name });
      }
    });
    actions.forEach((action) => {
      if (action.responsavel && action.responsavel !== '-' && !seen.has(action.responsavel)) {
        seen.add(action.responsavel);
        result.push({ name: action.responsavel });
      }
    });
    return result;
  }, [orgMembers, projectMembers, actions, organization]);

  const filteredUsers = availableUsers.filter((u) =>
    u.name.toLowerCase().includes(search.toLowerCase()),
  );

  useEffect(() => {
    const timer = window.setTimeout(() => inputRef.current?.focus(), 50);
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const handleSelect = (e: React.MouseEvent, name: string, userId?: string) => {
    e.preventDefault();
    e.stopPropagation();
    onSelectMember(name, userId);
    onClose();
  };

  return (
    <div
      className="absolute left-0 top-full z-50 mt-1 w-56 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 rounded shadow-lg"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="border-b border-slate-200 dark:border-slate-700 px-2 py-1.5">
        <input
          ref={inputRef}
          type="text"
          className="w-full border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1 text-[11px] text-slate-700 dark:text-slate-200 focus:border-slate-500 focus:outline-none"
          placeholder="Buscar..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.stopPropagation();
              onSelectMember(search);
              onClose();
            }
          }}
        />
      </div>
      <div className="max-h-40 overflow-y-auto py-1">
        {filteredUsers.map((u) => (
          <button
            key={u.name}
            onClick={(e) => handleSelect(e, u.name, u.userId)}
            className={`flex w-full items-center justify-between px-3 py-1.5 text-left text-[11px] ${
              currentValue === u.name
                ? 'bg-slate-100 dark:bg-slate-700 font-medium text-slate-800 dark:text-slate-100'
                : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/60'
            }`}
          >
            <span className="truncate">{u.name}</span>
            {currentValue === u.name && <CheckCircle2 size={11} />}
          </button>
        ))}
        {search && !filteredUsers.some((u) => u.name === search) && (
          <button
            onClick={(e) => handleSelect(e, search)}
            className="flex w-full items-center gap-1 border-t border-slate-200 dark:border-slate-700 px-3 py-1.5 text-left text-[11px] font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/60"
          >
            <Plus size={11} />
            Criar "{search}"
          </button>
        )}
      </div>
    </div>
  );
};

const ProgressBar = ({ action }: { action: Action }) => {
  if (!action) return null;
  const checklist = (action as any).checklist || action.actionPlan?.checklist || [];
  const total = checklist.length;
  if (total === 0) return null;
  const completed = checklist.filter((task: any) => task.checked).length;
  const percent = Math.round((completed / total) * 100);

  return (
    <div
      className="mt-1 flex w-full max-w-[132px] items-center gap-2"
      title={`${completed}/${total} tarefas`}
    >
      <List size={11} className="flex-shrink-0 text-slate-400" />
      <div className="h-1 w-full flex-1 overflow-hidden bg-slate-200 dark:bg-slate-700">
        <div
          className={percent === 100 ? 'bg-slate-500' : 'bg-blue-600'}
          style={{ width: `${percent}%`, height: '100%' }}
        />
      </div>
      <span className="min-w-[24px] whitespace-nowrap text-right text-[9px] font-medium text-slate-500">
        {percent}%
      </span>
    </div>
  );
};

const StatusBadge = ({
  status,
  onClick,
  isOverdue = false,
}: {
  status: KanbanTaskStatus;
  onClick: () => void;
  isOverdue?: boolean;
}) => {
  const config = {
    todo: { label: 'A Fazer', bg: 'bg-slate-100 dark:bg-slate-800', text: 'text-slate-700 dark:text-slate-300', border: 'border-slate-300 dark:border-slate-600', icon: Circle },
    doing: { label: 'Em Andamento', bg: 'bg-blue-50 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400', border: 'border-blue-200 dark:border-blue-800', icon: Clock },
    done: { label: 'Concluído', bg: 'bg-emerald-50 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-400', border: 'border-emerald-200 dark:border-emerald-800', icon: CheckCircle2 },
    canceled: { label: 'Cancelada', bg: 'bg-slate-100 dark:bg-slate-800', text: 'text-slate-500 dark:text-slate-500', border: 'border-slate-300 dark:border-slate-600', icon: X },
  };
  const current = config[status] || config.todo;
  const Icon = current.icon;

  // Estado crítico: mascara o status original e vira atalho para concluir
  if (isOverdue) {
    return (
      <button
        type="button"
        title="Ação atrasada — clique para marcar como concluída"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onClick();
        }}
        className="relative z-10 flex w-fit cursor-pointer items-center gap-1 whitespace-nowrap border px-2.5 py-1 text-[10px] font-bold bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800"
      >
        🔴 Atrasado
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }}
      className={`relative z-10 flex w-fit cursor-pointer items-center gap-1 whitespace-nowrap border px-2.5 py-1 text-[10px] font-semibold ${current.bg} ${current.text} ${current.border}`}
    >
      <Icon size={12} strokeWidth={2.25} className="flex-shrink-0" />
      {current.label}
    </button>
  );
};

const PriorityBadge = ({
  value,
  type,
}: {
  value?: number;
  type: 'impacto' | 'esforco';
}) => {
  const configs = {
    impacto: {
      1: { label: 'Baixo', bg: 'bg-emerald-50 dark:bg-emerald-900/20', text: 'text-emerald-700 dark:text-emerald-400', border: 'border-emerald-200 dark:border-emerald-800' },
      2: { label: 'Médio', bg: 'bg-amber-50 dark:bg-amber-900/20', text: 'text-amber-700 dark:text-amber-400', border: 'border-amber-200 dark:border-amber-800' },
      3: { label: 'Alto', bg: 'bg-rose-50 dark:bg-rose-900/20', text: 'text-rose-700 dark:text-rose-400', border: 'border-rose-200 dark:border-rose-800' },
    },
    esforco: {
      1: { label: 'Fácil', bg: 'bg-emerald-50 dark:bg-emerald-900/20', text: 'text-emerald-700 dark:text-emerald-400', border: 'border-emerald-200 dark:border-emerald-800' },
      2: { label: 'Médio', bg: 'bg-amber-50 dark:bg-amber-900/20', text: 'text-amber-700 dark:text-amber-400', border: 'border-amber-200 dark:border-amber-800' },
      3: { label: 'Difícil', bg: 'bg-rose-50 dark:bg-rose-900/20', text: 'text-rose-700 dark:text-rose-400', border: 'border-rose-200 dark:border-rose-800' },
    },
  };

  if (!value || value <= 0) {
    return <span className="text-[10px] text-slate-400 dark:text-slate-600">-</span>;
  }

  const config = configs[type][value as 1 | 2 | 3];

  return (
    <span className={`inline-flex items-center gap-1 border px-2 py-0.5 text-[10px] font-semibold ${config.bg} ${config.text} ${config.border}`}>
      {config.label}
    </span>
  );
};

const ScoreCell = ({
  impacto,
  esforco,
  onNavigateToMatrix,
}: {
  impacto?: number;
  esforco?: number;
  onNavigateToMatrix: () => void;
}) => {
  const hasScore = impacto && esforco && impacto > 0 && esforco > 0;

  if (hasScore) {
    const score = (impacto / esforco).toFixed(2);
    return (
      <span className={`text-[10px] font-semibold ${Number(score) >= 2 ? 'text-slate-700 dark:text-slate-200' : 'text-slate-500 dark:text-slate-400'}`}>
        {score}
      </span>
    );
  }

  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onNavigateToMatrix();
      }}
      className="inline-flex items-center gap-1 border border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-800 px-2 py-1 text-[10px] font-semibold text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
      title="Classificar esta acao na Matriz de Priorizacao"
    >
      Classificar
    </button>
  );
};

const InlineTextEdit = ({
  value,
  onSave,
  className,
  placeholder,
}: {
  value: string;
  onSave: (val: string) => void;
  className?: string;
  placeholder?: string;
}) => {
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => setLocalValue(value || ''), [value]);

  return (
    <input
      className={`w-full border border-transparent bg-transparent px-1 py-0.5 text-[12px] font-medium hover:border-slate-300 dark:hover:border-slate-600 focus:border-slate-500 focus:bg-white dark:focus:bg-slate-800 focus:outline-none ${className}`}
      value={localValue}
      placeholder={placeholder}
      onChange={(e) => setLocalValue(e.target.value)}
      onBlur={() => localValue !== value && onSave(localValue)}
      onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
      onClick={(e) => e.stopPropagation()}
    />
  );
};

const formatDateBR = (dateString?: string): string => {
  if (!dateString) return '';
  const date = new Date(`${dateString}T00:00:00`);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const DatePicker = ({
  value,
  onChange,
  action,
}: {
  value?: string;
  onChange: (date: string) => void;
  action?: Action;
}) => {
  const [editing, setEditing] = useState(false);
  const hasHistory = action?.deadlineHistory && action.deadlineHistory.length > 0;
  const originalDate = hasHistory ? action.deadlineHistory[0].oldDeadline : null;
  const isoValue = value ? new Date(`${value}T00:00:00`).toISOString().split('T')[0] : '';

  if (!value && !editing) {
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setEditing(true);
        }}
        className="px-1.5 py-0.5 text-[11px] font-medium text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
      >
        Sem prazo
      </button>
    );
  }

  return (
    <div className="relative flex w-full flex-col gap-0.5" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center gap-1">
        <input
          type="date"
          autoFocus={!value}
          className="w-full cursor-pointer border border-transparent bg-transparent px-1.5 py-0.5 text-[11px] font-medium text-slate-600 dark:text-slate-300 hover:border-slate-300 dark:hover:border-slate-600 focus:border-slate-500 focus:bg-white dark:focus:bg-slate-800 focus:outline-none"
          value={isoValue}
          onChange={(e) => {
            onChange(e.target.value);
            if (!e.target.value) setEditing(false);
          }}
          onBlur={(e) => {
            if (!e.target.value) setEditing(false);
          }}
        />
      </div>

      {hasHistory && originalDate && (
        <div className="flex items-center gap-1 px-1.5 text-[9px]">
          <span className="font-medium text-rose-500 dark:text-rose-400 line-through">{formatDateBR(originalDate)}</span>
          <span className="text-slate-400 dark:text-slate-500">-&gt;</span>
          <span className="font-medium text-slate-600 dark:text-slate-300">{formatDateBR(value)}</span>
        </div>
      )}
    </div>
  );
};

const SectorSelect = ({
  value,
  onChange,
}: {
  value?: string;
  onChange: (val: string) => void;
}) => {
  const sectors = ['Geral', 'PCP', 'Qualidade', 'Manutencao', 'Producao', 'Logistica', 'RH', 'Vendas'];
  return (
    <select
      className="w-full appearance-none border border-transparent bg-transparent px-1.5 py-0.5 text-[11px] font-medium text-slate-600 dark:text-slate-300 hover:border-slate-300 dark:hover:border-slate-600 focus:border-slate-500 focus:bg-white dark:focus:bg-slate-800 focus:outline-none"
      value={value || 'Geral'}
      onChange={(e) => onChange(e.target.value)}
      onClick={(e) => e.stopPropagation()}
    >
      {sectors.map((sector) => (
        <option key={sector} value={sector}>
          {sector}
        </option>
      ))}
    </select>
  );
};

const ResponsibleAvatar = ({
  name,
  onClick,
}: {
  name?: string;
  onClick?: () => void;
}) => {
  if (!name || name === '-' || name === '') {
    return (
      <div
        onClick={(e) => {
          e.stopPropagation();
          onClick?.();
        }}
        className="flex cursor-pointer items-center gap-1.5 border border-transparent px-1 py-0.5 text-slate-400 dark:text-slate-500 hover:border-slate-300 dark:hover:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-600 dark:hover:text-slate-300"
      >
        <div className="flex h-5 w-5 items-center justify-center border border-dashed border-slate-300 dark:border-slate-600">
          <UserPlus size={11} />
        </div>
        <span className="text-[10px] font-medium">Atribuir</span>
      </div>
    );
  }

  const colors = ['bg-slate-200 dark:bg-slate-700 text-blue-700 dark:text-blue-400', 'bg-slate-200 dark:bg-slate-700 text-emerald-700 dark:text-emerald-400', 'bg-slate-200 dark:bg-slate-700 text-violet-700 dark:text-violet-400', 'bg-slate-200 dark:bg-slate-700 text-amber-700 dark:text-amber-400'];
  const colorClass = colors[(name.charCodeAt(0) || 0) % colors.length];

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      className="flex cursor-pointer items-center gap-2 border border-transparent px-1 py-0.5 hover:border-slate-300 dark:hover:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800"
    >
      <div className={`flex h-5 w-5 items-center justify-center text-[9px] font-bold ${colorClass}`}>
        {name.slice(0, 2).toUpperCase()}
      </div>
      <span className="max-w-[120px] truncate text-[10px] font-medium text-slate-700 dark:text-slate-300">{name}</span>
    </div>
  );
};

export const ActionListView: React.FC<ActionListViewProps> = ({
  actions,
  cards,
  onActionClick,
  onUpdateAction,
  activeTab,
  groupBy,
  onContextMenu,
  selectedActionIds = new Set(),
  onNavigateToMatrix,
  onNavigateToCard,
  zoom = 1,
  focusedColumn,
}) => {
  const { user } = useAuth();
  const organizationId = (user as any)?.organizationId ?? (user as any)?.organization_id;
  const { members: orgMembers } = useOrgMembers(organizationId);
  const { moveKanbanTask, updateCardData, projects } = useProjectStore();
  const [projectMembers, setProjectMembers] = useState<OrgMember[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [assignPopoverId, setAssignPopoverId] = useState<string | null>(null);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const tableViewportRef = useRef<HTMLDivElement>(null);
  // Badge [ID: ...] é diagnóstico interno; só aparece quando ativado manualmente
  // via `localStorage.setItem('wf:debug:vsm-action-id', '1')`.
  const isIdempotencyDebugMode = typeof window !== 'undefined'
    && window.localStorage?.getItem('wf:debug:vsm-action-id') === '1';
  const hasAutoFitColumnsRef = useRef(false);
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({
    numero: 44,
    status: 104,
    title: 420,
    projeto: 96,
    responsavel: 118,
    prazo: 88,
    origem: 84,
    custo: 68,
    impacto: 64,
    esforco: 64,
  });
  const minColumnWidths: Record<string, number> = useMemo(() => ({
    numero: 36,
    status: 88,
    title: 180,
    projeto: 76,
    responsavel: 96,
    prazo: 78,
    origem: 68,
    custo: 58,
    impacto: 56,
    esforco: 56,
  }), []);
  const [resizing, setResizing] = useState<{
    column: string;
    startX: number;
    startWidth: number;
  } | null>(null);

  useEffect(() => {
    const element = tableViewportRef.current;
    if (!element) return;

    const fitColumnsToContainer = () => {
      if (hasAutoFitColumnsRef.current) return;
      const availableWidth = Math.floor(element.clientWidth);
      if (availableWidth <= 0) return;
      setColumnWidths((prev) => {
        const currentWidth = Object.values(prev).reduce((total, width) => total + width, 0);
        if (currentWidth >= availableWidth) return prev;
        hasAutoFitColumnsRef.current = true;
        return { ...prev, title: prev.title + (availableWidth - currentWidth) };
      });
    };

    fitColumnsToContainer();
    const observer = new ResizeObserver(fitColumnsToContainer);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!resizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const delta = e.clientX - resizing.startX;
      setColumnWidths((prev) => {
        const currentWidth = prev[resizing.column] ?? resizing.startWidth;
        const newWidth = Math.max(minColumnWidths[resizing.column] ?? 60, resizing.startWidth + delta);
        const appliedDelta = newWidth - currentWidth;
        if (appliedDelta === 0) return prev;

        const compensationColumn = resizing.column === 'title' ? 'projeto' : 'title';
        const compensationWidth = prev[compensationColumn] ?? 0;
        const nextCompensationWidth = Math.max(
          minColumnWidths[compensationColumn] ?? 60,
          compensationWidth - appliedDelta,
        );
        const uncompensatedDelta = appliedDelta - (compensationWidth - nextCompensationWidth);
        return {
          ...prev,
          [resizing.column]: newWidth - Math.max(0, uncompensatedDelta),
          [compensationColumn]: nextCompensationWidth,
        };
      });
    };

    const handleMouseUp = () => setResizing(null);

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [minColumnWidths, resizing]);

  useEffect(() => {
    const projectIds = [...new Set(actions.map(action => action.projectId).filter(Boolean))] as string[];
    if (projectIds.length === 0) {
      setProjectMembers([]);
      return;
    }

    let cancelled = false;
    Promise.all(projectIds.slice(0, 3).map(projectId => ProjectService.listMembers(projectId)))
      .then(memberGroups => {
        if (cancelled) return;
        const seen = new Set<string>();
        const nextMembers: OrgMember[] = [];
        memberGroups.flat().forEach(member => {
          if (seen.has(member.user_id)) return;
          const orgMember = orgMembers.find(item => item.id === member.user_id);
          const name = member.user_name ?? orgMember?.name ?? member.user_email ?? orgMember?.email ?? member.user_id;
          seen.add(member.user_id);
          nextMembers.push({ id: member.user_id, name, email: member.user_email ?? orgMember?.email ?? '' } as any);
        });
        setProjectMembers(nextMembers);
      })
      .catch(() => {
        if (!cancelled) setProjectMembers([]);
      });

    return () => { cancelled = true; };
  }, [actions, orgMembers]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      return;
    }
    setSortColumn(column);
    setSortDirection('asc');
  };

  const handleStatusClick = (action: Action, isOverdue = false) => {
    // Atalho inteligente: ação atrasada é concluída diretamente; senão, cicla o status
    const next: KanbanTaskStatus = isOverdue
      ? 'done'
      : action.status === 'todo' ? 'doing' : action.status === 'doing' ? 'done' : 'todo';
    const updatedPlan = { ...(action.actionPlan || {}), status: next };
    onUpdateAction(action.id, { status: next, actionPlan: updatedPlan as any });
    moveKanbanTask(action.id, next);
    if (action.responsibleUserId && action.projectId && organizationId) {
      syncPlanActionToSupabase(
        { ...action, status: next },
        organizationId,
        action.projectId,
        action.responsibleUserId,
      ).catch(() => {});
    }
  };

  const handleSmartUpdate = (action: Action, updates: Partial<Action>) => {
    const currentPlan = action.actionPlan || {};
    const newActionPlan = { ...currentPlan };

    if (updates.responsavel !== undefined) newActionPlan.quem = updates.responsavel;
    if (updates.prazo !== undefined) newActionPlan.quando = updates.prazo;

    if (updates.prazo !== undefined && updates.prazo !== action.prazo) {
      const now = new Date().toISOString();
      const createdAt = action.createdAt || now;
      const creationDate = createdAt.split('T')[0];
      const today = new Date().toISOString().split('T')[0];

      if (creationDate !== today && action.prazo) {
        const currentHistory = action.deadlineHistory || [];

        if (currentHistory.length === 0) {
          updates.deadlineHistory = [
            {
              oldDeadline: action.prazo,
              newDeadline: updates.prazo,
              changedAt: now,
              changedBy: user?.email || 'unknown',
              reason: 'Data postergada',
            },
          ];
          updates.originalDeadline = action.prazo;
        } else {
          updates.deadlineHistory = currentHistory;
        }
      }
    }

    onUpdateAction(action.id, { ...updates, actionPlan: newActionPlan as any });
    if (action.linkedNodeId) updateCardData(action.linkedNodeId, updates as any);
    if (updates.responsibleUserId && action.projectId && organizationId) {
      const merged = { ...action, ...updates } as Action;
      syncPlanActionToSupabase(merged, organizationId, action.projectId, updates.responsibleUserId).catch(() => {});
      // Send email notification to the assigned member
      const member = orgMembers.find((m) => m.id === updates.responsibleUserId);
      if (member?.email) {
        const projectName = projects.find((p) => p.id === action.projectId)?.nome_projeto ?? '';
        import('../services/emailService').then(({ sendActionAssignedEmail }) => {
          sendActionAssignedEmail(member.email, {
            userName: member.name,
            actionTitle: merged.title,
            projectName,
            deadline: merged.prazo,
            actionUrl: `${window.location.origin}/acoes`,
          });
        });
      }
    }
  };

  const processedActions = useMemo(() => {
    let result = [...actions];
    const originalIndexById = new Map(actions.map((action, index) => [action.id, index]));
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (activeTab === 'mine') {
      const userName = (user as any)?.user_metadata?.name || user?.email || '';
      result = result.filter(
        (action) => action.responsavel && userName.toLowerCase().includes(action.responsavel.toLowerCase()),
      );
    } else if (activeTab === 'overdue') {
      result = result.filter((action) => action.status !== 'done' && action.prazo && new Date(action.prazo) < today);
    } else if (activeTab === 'done') {
      result = result.filter((action) => action.status === 'done');
    } else if (activeTab === 'doing') {
      result = result.filter((action) => action.status === 'doing');
    }

    if (sortColumn) {
      result.sort((a, b) => {
        let valA: string | number = '';
        let valB: string | number = '';

        switch (sortColumn) {
          case 'numero':
            valA = originalIndexById.get(a.id) ?? Number.MAX_SAFE_INTEGER;
            valB = originalIndexById.get(b.id) ?? Number.MAX_SAFE_INTEGER;
            break;
          case 'status': {
            // Ações atrasadas mascaram o status na UI ("🔴 Atrasado"); ordenam como nível próprio
            // para agruparem juntas, refletindo o que é exibido na coluna.
            const statusOrder = { todo: 2, doing: 3, done: 4, canceled: 5 } as Record<string, number>;
            valA = isActionOverdue(a) ? 1 : (statusOrder[a.status] || 0);
            valB = isActionOverdue(b) ? 1 : (statusOrder[b.status] || 0);
            break;
          }
          case 'titulo':
          case 'title':
            valA = (a.title || '').toLowerCase();
            valB = (b.title || '').toLowerCase();
            break;
          case 'projeto':
            valA = (a._projectName || '').toLowerCase();
            valB = (b._projectName || '').toLowerCase();
            break;
          case 'responsavel':
            valA = (a.responsavel || '').toLowerCase();
            valB = (b.responsavel || '').toLowerCase();
            break;
          case 'prazo':
            valA = a.prazo ? new Date(a.prazo).getTime() : Number.MAX_SAFE_INTEGER;
            valB = b.prazo ? new Date(b.prazo).getTime() : Number.MAX_SAFE_INTEGER;
            break;
          case 'origem': {
            const originA = a.originSource || (a.linkedNodeId ? 'Canvas' : 'Manual');
            const originB = b.originSource || (b.linkedNodeId ? 'Canvas' : 'Manual');
            valA = String(originA).toLowerCase();
            valB = String(originB).toLowerCase();
            break;
          }
          case 'custo':
            valA = a.cost ?? Number.MAX_SAFE_INTEGER;
            valB = b.cost ?? Number.MAX_SAFE_INTEGER;
            break;
          case 'setor':
            valA = (a.cardData?.setor || 'Geral').toLowerCase();
            valB = (b.cardData?.setor || 'Geral').toLowerCase();
            break;
          case 'impacto':
            valA = a.impacto || 0;
            valB = b.impacto || 0;
            break;
          case 'esforco':
            valA = a.esforco || 0;
            valB = b.esforco || 0;
            break;
          case 'score':
            valA = a.impacto && a.esforco ? a.impacto / a.esforco : 0;
            valB = b.impacto && b.esforco ? b.impacto / b.esforco : 0;
            break;
          default:
            break;
        }

        if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
        if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    } else {
      const rank = (action: Action) => {
        if (isActionOverdue(action)) return 0;
        if (action.status === 'canceled') return 4;
        if (action.status === 'done') return 3;
        const owner = (action.responsavel || action.responsible || '').trim();
        return owner ? 2 : 1;
      };
      result.sort((a, b) => {
        const left = rank(a);
        const right = rank(b);
        if (left !== right) return left - right;
        if (left === 0) {
          const leftDue = parseActionDeadline(a.prazo)?.getTime() ?? parseActionDeadline(a.deadline)?.getTime() ?? 0;
          const rightDue = parseActionDeadline(b.prazo)?.getTime() ?? parseActionDeadline(b.deadline)?.getTime() ?? 0;
          return leftDue - rightDue;
        }
        return (originalIndexById.get(a.id) ?? 0) - (originalIndexById.get(b.id) ?? 0);
      });
    }

    return result;
  }, [actions, activeTab, sortColumn, sortDirection, user]);

  const groupedActions = useMemo(() => {
    if (groupBy === 'none') return { 'Todas as Acoes': processedActions };

    return processedActions.reduce((groups, action) => {
      let key = 'Outros';
      if (groupBy === 'status') {
        key = action.status === 'todo' ? 'A Fazer' : action.status === 'doing' ? 'Em Andamento' : 'Concluido';
      } else if (groupBy === 'responsavel') {
        key = action.responsavel || 'Sem Responsavel';
      } else if (groupBy === 'impacto') {
        key = action.impacto === 3 ? 'Alto Impacto' : action.impacto === 2 ? 'Medio Impacto' : 'Baixo Impacto';
      }

      if (!groups[key]) groups[key] = [];
      groups[key].push(action);
      return groups;
    }, {} as Record<string, Action[]>);
  }, [groupBy, processedActions]);

  useEffect(() => {
    if (Object.keys(groupedActions).length === 0) return;
    setExpandedGroups((prev) => {
      if (Object.keys(prev).length > 0) return prev;
      return Object.keys(groupedActions).reduce(
        (acc, key) => ({ ...acc, [key]: true }),
        {} as Record<string, boolean>,
      );
    });
  }, [groupedActions]);

  useEffect(() => {
    const handleGlobalClick = () => setAssignPopoverId(null);
    window.addEventListener('click', handleGlobalClick);
    return () => window.removeEventListener('click', handleGlobalClick);
  }, []);

  const toggleGroup = (group: string) => {
    setExpandedGroups((prev) => ({ ...prev, [group]: !prev[group] }));
  };

  const getCardInfo = (linkedId?: string) => {
    if (!linkedId) return null;
    const card = cards.find((item) => item.id === linkedId);
    return card ? { num: card.numeroSequencial, nome: card.atividade } : null;
  };

  const SortableHeader = ({
    column,
    children,
    className = '',
    width,
  }: {
    column: string;
    children: React.ReactNode;
    className?: string;
    width?: number;
  }) => (
    <th
      className={`group relative cursor-pointer select-none px-3 py-2 font-mono text-[10px] font-medium uppercase tracking-[0.14em] ${className}`}
      onClick={() => handleSort(column)}
      style={{ minWidth: width ? `${width}px` : undefined }}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortColumn === column &&
          (sortDirection === 'asc' ? (
            <ArrowUp size={11} className="enterprise-text-primary" />
          ) : (
            <ArrowDown size={11} className="enterprise-text-primary" />
          ))}
      </div>
      <div
        className="absolute bottom-0 right-0 top-0 w-1 cursor-col-resize group-hover:bg-[var(--border-default)]"
        onMouseDown={(e) => {
          e.stopPropagation();
          setResizing({ column, startX: e.clientX, startWidth: columnWidths[column] || 100 });
        }}
      />
    </th>
  );

  const fontSizePercent = Math.round(80 + (zoom - 0.5) * 40);
  const columnOrder = ['numero', 'status', 'title', 'projeto', 'responsavel', 'prazo', 'origem', 'custo', 'impacto', 'esforco'];
  const tableWidth = columnOrder.reduce((total, column) => total + (columnWidths[column] ?? 0), 0);

  return (
    <div
      className="flex h-full w-full flex-col overflow-hidden"
      onClick={(e) => e.stopPropagation()}
      style={{ fontSize: `${fontSizePercent}%` }}
    >
      <div ref={tableViewportRef} className="flex-1 overflow-x-auto overflow-y-auto">
      <div className="enterprise-table-shell min-w-full">
      <table className="w-full table-fixed text-xs" style={{ minWidth: `${tableWidth}px` }}>
        <colgroup>
          {columnOrder.map((column) => (
            <col key={column} style={{ width: `${columnWidths[column]}px` }} />
          ))}
        </colgroup>
        <thead className="enterprise-table-header sticky top-0 z-10 text-left font-mono text-[10px] uppercase tracking-[0.14em]">
          <tr>
            <SortableHeader column="numero" width={columnWidths.numero} className="text-center">#</SortableHeader>
            <SortableHeader column="status" width={columnWidths.status}>Status</SortableHeader>
            <SortableHeader column="titulo" width={columnWidths.title}>Ação</SortableHeader>
            <SortableHeader column="projeto" width={columnWidths.projeto}>Projeto</SortableHeader>
            <SortableHeader column="responsavel" width={columnWidths.responsavel} className={focusedColumn === 'responsavel' ? 'bg-cyan-500/10 text-cyan-300' : undefined}>Responsável</SortableHeader>
            <SortableHeader column="prazo" width={columnWidths.prazo} className={focusedColumn === 'prazo' ? 'bg-cyan-500/10 text-cyan-300' : undefined}>Prazo</SortableHeader>
            <SortableHeader column="origem" width={columnWidths.origem}>Origem</SortableHeader>
            <SortableHeader column="custo" width={columnWidths.custo} className="text-right">Custo (R$)</SortableHeader>
            <SortableHeader column="impacto" width={columnWidths.impacto} className="text-center">Impacto</SortableHeader>
            <SortableHeader column="esforco" width={columnWidths.esforco} className="text-center">Esforço</SortableHeader>
          </tr>
        </thead>
        <tbody>
          {(Object.entries(groupedActions) as [string, any[]][]).map(([groupName, groupActions]) => (
            <React.Fragment key={groupName}>
              {groupBy !== 'none' && (
                <tr className="enterprise-table-row bg-[var(--bg-elevated)]">
                  <td colSpan={10} className="px-3 py-1.5">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleGroup(groupName);
                      }}
                      className="flex w-full items-center gap-2 text-left text-[11px] font-semibold text-[var(--text-primary)]"
                    >
                      {expandedGroups[groupName] ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                      {groupName}
                      <span className="border border-[var(--border-default)] bg-[var(--bg-surface)] px-1.5 rounded text-[9px] enterprise-text-secondary">{groupActions.length}</span>
                    </button>
                  </td>
                </tr>
              )}
              {(groupBy === 'none' || expandedGroups[groupName]) &&
                groupActions.map((action) => {
                  const cardInfo = getCardInfo(action.linkedNodeId);
                  const isHovered = hoveredId === action.id;
                  const isSelected = selectedActionIds.has(action.id);
                  const isQuickWin = action.impacto === 3 && action.esforco === 1;
                  const isCanceled = action.status === 'canceled';
                  const isOverdue = isActionOverdue(action);
                  const actionNumber = processedActions.findIndex((item) => item.id === action.id) + 1;

                  // ── Origem badge (Poka-Yoke) ──────────────────────────
                  const originSource = action.linkedNodeId ? (action.originSource ?? 'Canvas') : 'Manual';
                  const originLabel =
                    originSource === 'Canvas' || originSource === 'PostIt'
                      ? cardInfo
                        ? `VSM · ${cardInfo.num}`
                        : 'VSM'
                      : originSource === 'Problema'
                        ? 'Problema'
                        : originSource === 'Kanban'
                          ? 'Kanban'
                          : originSource === 'Auditoria'
                            ? 'Auditoria'
                            : originSource === 'Import'
                              ? 'Import'
                              : 'Manual';
                  const originIcon =
                    originSource === 'Canvas' || originSource === 'PostIt' ? (
                      <MapIcon size={10} className="shrink-0" />
                    ) : originSource === 'Problema' ? (
                      <AlertTriangle size={10} className="shrink-0" />
                    ) : originSource === 'Kanban' ? (
                      <Workflow size={10} className="shrink-0" />
                    ) : null;
                  const originColor =
                    originSource === 'Canvas' || originSource === 'PostIt'
                      ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-400'
                      : originSource === 'Problema'
                        ? 'border-rose-500/40 bg-rose-500/10 text-rose-400'
                        : originSource === 'Auditoria'
                          ? 'border-amber-500/40 bg-amber-500/10 text-amber-400'
                          : 'border-slate-500/30 bg-slate-500/10 text-slate-400';
                          const canOpenOrigin = Boolean(action.linkedNodeId && onNavigateToCard);

                  return (
                    <tr
                      key={action.id}
                      onClick={(e) => onActionClick(action.id, e)}
                      onContextMenu={(e) => onContextMenu?.(action.id, e)}
                      onMouseEnter={() => setHoveredId(action.id)}
                      onMouseLeave={() => setHoveredId(null)}
                      className={`enterprise-table-row cursor-pointer ${
                        isCanceled
                          ? 'opacity-60'
                          : isSelected
                            ? 'is-selected'
                            : ''
                      } ${isQuickWin && !isCanceled ? 'bg-blue-50/30 dark:bg-blue-900/10' : ''} ${
                        isOverdue && !isCanceled ? 'bg-red-50/40 dark:bg-red-900/10' : ''
                      }`}
                    >
                      {/* # */}
                      <td className="px-2 py-2 align-middle text-center">
                        <span className="inline-flex h-5 w-5 items-center justify-center enterprise-surface-3 rounded text-[10px] font-mono font-semibold enterprise-text-secondary">{actionNumber}</span>
                      </td>
                      {/* Status */}
                      <td className="px-2 py-2 align-middle">
                        <StatusBadge
                          status={action.status}
                          isOverdue={isOverdue}
                          onClick={() => handleStatusClick(action, isOverdue)}
                        />
                      </td>
                      {/* Ação / Título */}
                      <td className="w-auto px-2 py-2 align-middle">
                        <div className="flex min-h-[26px] w-full flex-col justify-center">
                          <div className="flex w-full items-center gap-1.5">
                            {isQuickWin && !isCanceled && <Star size={10} className="flex-shrink-0 fill-slate-500 text-slate-500" />}
                            {isCanceled && <span className="mr-1 text-[10px] text-slate-400">✕</span>}
                            <InlineTextEdit
                              value={action.title}
                              onSave={(val) => onUpdateAction(action.id, { title: val })}
                              className={`${isCanceled ? 'text-slate-400 dark:text-slate-500 line-through' : 'text-slate-700 dark:text-slate-200'} w-full font-medium`}
                              placeholder="Descreva a ação..."
                            />
                            {isIdempotencyDebugMode && action.sourceComponentId && (
                              <span
                                className="inline-flex max-w-[280px] shrink-0 truncate rounded border border-slate-400/40 bg-slate-500/15 px-1.5 py-0.5 font-mono text-[9px] font-medium text-slate-500"
                                title={`ID: ${action.sourceComponentId}`}
                              >
                                {`[ID: ${action.sourceComponentId}]`}
                              </span>
                            )}
                          </div>
                          <ProgressBar action={action} />
                        </div>
                      </td>
                      {/* Projeto */}
                      <td className="px-2 py-2 align-middle">
                        {action._projectName ? (
                          <span className="inline-flex max-w-full items-center gap-1 truncate rounded border border-[var(--border-default)] bg-[var(--bg-elevated)] px-1.5 py-0.5 text-[10px] font-medium text-[var(--text-secondary)]" title={action._projectName}>
                            {action._projectName}
                          </span>
                        ) : (
                          <span className="text-[10px] enterprise-text-muted">—</span>
                        )}
                      </td>
                      {/* Responsável */}
                      <td className={`relative px-2 py-2 align-middle ${focusedColumn === 'responsavel' ? 'bg-cyan-500/5 ring-1 ring-inset ring-cyan-500/10' : ''}`}>
                        <ResponsibleAvatar
                          name={action.responsavel}
                          onClick={() => setAssignPopoverId(assignPopoverId === action.id ? null : action.id)}
                        />
                        {assignPopoverId === action.id && (
                          <div onClick={(e) => e.stopPropagation()}>
                            <UserAssignmentPopover
                              currentValue={action.responsavel}
                              orgMembers={orgMembers}
                              projectMembers={projectMembers}
                              onClose={() => setAssignPopoverId(null)}
                              onSelectMember={(name, userId) => handleSmartUpdate(action, { responsavel: name, responsibleUserId: userId })}
                            />
                          </div>
                        )}
                      </td>
                      {/* Prazo */}
                      <td className={`px-2 py-2 align-middle ${focusedColumn === 'prazo' ? 'bg-cyan-500/5 ring-1 ring-inset ring-cyan-500/10' : ''} ${isOverdue ? 'bg-red-50/60 dark:bg-red-900/15' : ''}`}>
                        <div className={isOverdue ? 'text-red-600 dark:text-red-400 font-bold' : ''}>
                          <DatePicker value={action.prazo} action={action} onChange={(date) => handleSmartUpdate(action, { prazo: date })} />
                        </div>
                      </td>
                      {/* Origem — Poka-Yoke badge */}
                      <td className="px-2 py-2 align-middle">
                        <button
                          type="button"
                          disabled={!canOpenOrigin}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (action.linkedNodeId) onNavigateToCard?.(action.linkedNodeId);
                          }}
                          className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-medium ${originColor} ${canOpenOrigin ? 'cursor-pointer hover:border-blue-400 hover:text-blue-300' : 'cursor-default opacity-80'}`}
                          title={canOpenOrigin ? 'Abrir origem no mapa VSM' : 'Ação manual sem card de origem'}
                        >
                          {originIcon}
                          {originLabel}
                          {canOpenOrigin && <span className="ml-0.5 opacity-70">↗</span>}
                        </button>
                      </td>
                      {/* Custo */}
                      <td className="px-2 py-2 align-middle text-right">
                        {action.cost != null ? (
                          <span className="inline-flex items-center gap-0.5 font-mono text-[11px] text-emerald-400">
                            <DollarSign size={10} />
                            {action.cost.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                          </span>
                        ) : (
                          <span className="text-[10px] enterprise-text-muted">—</span>
                        )}
                      </td>
                      {/* Impacto */}
                      <td className="px-2 py-2 align-middle text-center"><div className="flex justify-center"><PriorityBadge value={action.impacto} type="impacto" /></div></td>
                      {/* Esforço */}
                      <td className="px-2 py-2 align-middle text-center"><div className="flex justify-center"><PriorityBadge value={action.esforco} type="esforco" /></div></td>
                    </tr>
                  );
                })}
            </React.Fragment>
          ))}
        </tbody>
      </table>
      </div>
      </div>
    </div>
  );
};

export default ActionListView;
